import React, { HTMLAttributes, forwardRef } from 'react';

import cx from 'classnames';
import { get } from 'lodash';

import styles from './Typography.module.scss';

interface TypographyProps extends HTMLAttributes<HTMLElement> {
	as: 'h1' | 'h2' | 'h3' | 'h4' | 'body' | 'footnote';
	weight: 'regular' | 'medium' | 'semibold' | 'bold';
}

const BlockElements = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	body: 'p',
} as const;

const TypeElementMappings = {
	...BlockElements,
	footnote: 'span',
} as const;

type TypographyElement =
	(typeof TypeElementMappings)[keyof typeof TypeElementMappings];

/**
 * @description UI component for typography
 *
 * @example
 * <Typography as="h2" weight="bold">My Heading</Typgraphy>
 *
 * @author  Rohit Niraj Singh <@rohit.singh3>
 *
 * @param {TypographyProps} props
 *
 * @returns {React.ReactElement}
 */
const Typography = forwardRef<HTMLHeadingElement, TypographyProps>(
	(
		{ as, children, weight = 'regular', className, ...props }: TypographyProps,
		ref,
	) => {
		const isBlockElement = !!get(BlockElements, as);
		const Element = TypeElementMappings[as] as TypographyElement;
		return (
			<Element
				{...props}
				ref={ref}
				className={cx(
					styles.typography,
					isBlockElement && styles.block,
					styles[as],
					styles[weight],
					className,
				)}
			>
				{children}
			</Element>
		);
	},
);

Typography.displayName = 'Typography';

export default Typography;
